import { useState, useCallback } from 'react';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Modal } from '@/components/ui/Modal';
import { Badge } from '@/components/ui/Badge';
import { Dialog } from '@/components/ui/Dialog';
import { Tabs } from '@/components/ui/Tabs';
import { useToast } from '@/components/ui/Toast';
import { usePermissions, PERMISSIONS } from '@/hooks/usePermissions';
import { ContactPicker } from './ContactPicker';
import { GroupInvoiceModal } from './GroupInvoiceModal';
import {
  useContactGroups,
  useContactGroup,
  useGroupJobs,
  useGroupInvoices,
  useGroupLedger,
  useGroupTypes,
  useCreateContactGroup,
  useUpdateContactGroup,
  useDeleteContactGroup,
  useAddGroupMember,
  useRemoveGroupMember,
  useRelationshipTypes,
  useSetMemberPortalRole,
  useSetGroupPortalAccess,
  useSetGroupPortalMfa,
  useGrantPortalAccess,
  type ContactGroupSummary,
  type GroupMember,
  type GroupListFilters,
} from './hooks/useContactGroups';

const money = (n: number | null | undefined) =>
  `$${Number(n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * ContactGroupsPage — manage contact groups (relationships/groupings) and use
 * them across jobs, consolidated invoicing, and combined ledgers.
 */
export function ContactGroupsPage() {
  const [search, setSearch] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<GroupListFilters>({});
  const { data, isLoading } = useContactGroups({ ...filters, search: search || undefined });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<ContactGroupSummary | null>(null);
  const { can } = usePermissions();
  const { data: groupTypes } = useGroupTypes();

  const activeFilterCount = Object.values(filters).filter(
    (v) => v !== undefined && v !== '' && v !== null,
  ).length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Groups &amp; Relationships
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Group related contacts to manage jobs, consolidated invoicing, and combined ledgers.
          </p>
        </div>
        {can(PERMISSIONS.GROUPS_CREATE) && (
          <Button onClick={() => setShowCreate(true)}>Create Group</Button>
        )}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-4">
        <div className="lg:col-span-1">
          <Card padding="none">
            <div className="space-y-2 border-b border-border p-3 dark:border-border-dark">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Groups ({data?.total ?? 0})
                </h2>
                <button
                  type="button"
                  onClick={() => setShowFilters((v) => !v)}
                  className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium transition-colors ${
                    showFilters || activeFilterCount > 0
                      ? 'bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300'
                      : 'text-gray-500 hover:bg-surface-secondary dark:text-gray-400 dark:hover:bg-surface-dark-secondary'
                  }`}
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    aria-hidden="true"
                  >
                    <path d="M3 5h18M6 12h12M10 19h4" strokeLinecap="round" />
                  </svg>
                  Filters
                  {activeFilterCount > 0 && (
                    <span className="ml-0.5 rounded-full bg-brand-600 px-1.5 text-[10px] leading-4 text-white">
                      {activeFilterCount}
                    </span>
                  )}
                </button>
              </div>
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name or description..."
              />
            </div>

            {showFilters && (
              <GroupFilters
                filters={filters}
                onChange={setFilters}
                groupTypes={groupTypes ?? []}
                onClear={() => setFilters({})}
              />
            )}

            <div className="max-h-[70vh] overflow-y-auto">
              {isLoading ? (
                <div className="p-4 text-center text-sm text-gray-500">Loading...</div>
              ) : (data?.groups.length ?? 0) === 0 ? (
                <div className="p-6 text-center">
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {activeFilterCount > 0 || search
                      ? 'No groups match your filters.'
                      : 'No groups yet.'}
                  </p>
                </div>
              ) : (
                <ul role="list" className="divide-y divide-border dark:divide-border-dark">
                  {data?.groups.map((group) => (
                    <li key={group.id}>
                      <button
                        type="button"
                        onClick={() => setSelectedId(group.id)}
                        className={`flex w-full items-center gap-2 px-3 py-2 text-left transition-colors hover:bg-surface-secondary dark:hover:bg-surface-dark-secondary ${
                          selectedId === group.id ? 'bg-brand-50 dark:bg-brand-900/20' : ''
                        }`}
                      >
                        <span className="min-w-0 flex-1 truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                          {group.name}
                        </span>
                        {group.portal_access_blocked && (
                          <span
                            className="h-2 w-2 shrink-0 rounded-full bg-danger-500"
                            title="Portal access blocked"
                          />
                        )}
                        {group.has_portal_manager && (
                          <span
                            className="h-2 w-2 shrink-0 rounded-full bg-warning-500"
                            title="Has a portal manager"
                          />
                        )}
                        <Badge variant="default" size="sm">
                          {group.member_count}
                        </Badge>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </Card>
        </div>

        <div className="lg:col-span-3">
          {selectedId ? (
            <GroupDetailPanel
              groupId={selectedId}
              onEdit={(g) => setEditing(g)}
              onDeleted={() => setSelectedId(null)}
            />
          ) : (
            <Card>
              <div className="py-12 text-center text-sm text-gray-500 dark:text-gray-400">
                Select a group to view members, jobs, invoices, and its combined ledger.
              </div>
            </Card>
          )}
        </div>
      </div>

      {showCreate && <GroupFormModal open onClose={() => setShowCreate(false)} />}
      {editing && <GroupFormModal open group={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

// ─── Group filters ─────────────────────────────────────────────────────────

/** A tri-state select mapping 'any' | 'true' | 'false' onto an optional boolean. */
function triState(v: boolean | undefined): 'any' | 'true' | 'false' {
  return v === undefined ? 'any' : v ? 'true' : 'false';
}
function fromTriState(v: string): boolean | undefined {
  return v === 'any' ? undefined : v === 'true';
}

function GroupFilters({
  filters,
  onChange,
  groupTypes,
  onClear,
}: {
  filters: GroupListFilters;
  onChange: (f: GroupListFilters) => void;
  groupTypes: Array<{ code: string; label: string }>;
  onClear: () => void;
}) {
  const set = (patch: Partial<GroupListFilters>) => onChange({ ...filters, ...patch });
  const hasAny = Object.values(filters).some((v) => v !== undefined && v !== '' && v !== null);

  return (
    <div className="space-y-3 border-b border-border bg-surface-secondary/50 p-3 dark:border-border-dark dark:bg-surface-dark-secondary/40">
      <Select
        label="Group type"
        value={filters.group_type ?? ''}
        onChange={(e) => set({ group_type: e.target.value || undefined })}
        placeholder="Any type"
        options={groupTypes.map((t) => ({ value: t.code, label: t.label }))}
      />
      <Select
        label="Status"
        value={triState(filters.is_active)}
        onChange={(e) => set({ is_active: fromTriState(e.target.value) })}
        options={[
          { value: 'any', label: 'Any status' },
          { value: 'true', label: 'Active' },
          { value: 'false', label: 'Inactive' },
        ]}
      />
      <Select
        label="Portal access"
        value={triState(filters.portal_access_blocked)}
        onChange={(e) => set({ portal_access_blocked: fromTriState(e.target.value) })}
        options={[
          { value: 'any', label: 'Any' },
          { value: 'true', label: 'Blocked' },
          { value: 'false', label: 'Allowed' },
        ]}
      />
      <Select
        label="Portal MFA"
        value={filters.portal_mfa ?? 'any'}
        onChange={(e) =>
          set({
            portal_mfa:
              e.target.value === 'any' ? undefined : (e.target.value as 'on' | 'off' | 'default'),
          })
        }
        options={[
          { value: 'any', label: 'Any' },
          { value: 'on', label: 'Always on' },
          { value: 'off', label: 'Disabled' },
          { value: 'default', label: 'Platform default' },
        ]}
      />
      <Select
        label="Billing contact"
        value={triState(filters.has_billing_contact)}
        onChange={(e) => set({ has_billing_contact: fromTriState(e.target.value) })}
        options={[
          { value: 'any', label: 'Any' },
          { value: 'true', label: 'Has billing contact' },
          { value: 'false', label: 'No billing contact' },
        ]}
      />
      <Select
        label="Members"
        value={triState(filters.has_members)}
        onChange={(e) => set({ has_members: fromTriState(e.target.value) })}
        options={[
          { value: 'any', label: 'Any' },
          { value: 'true', label: 'Has members' },
          { value: 'false', label: 'Empty groups' },
        ]}
      />
      <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
        <input
          type="checkbox"
          checked={!!filters.has_portal_manager}
          onChange={(e) => set({ has_portal_manager: e.target.checked || undefined })}
        />
        Has a portal manager
      </label>
      {hasAny && (
        <Button variant="ghost" size="sm" onClick={onClear} className="w-full">
          Clear filters
        </Button>
      )}
    </div>
  );
}

// ─── Detail panel ────────────────────────────────────────────────────────────

function GroupDetailPanel({
  groupId,
  onEdit,
  onDeleted,
}: {
  groupId: string;
  onEdit: (g: ContactGroupSummary) => void;
  onDeleted: () => void;
}) {
  const { data: group, isLoading } = useContactGroup(groupId);
  const [tab, setTab] = useState('members');
  const [showInvoice, setShowInvoice] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const { can } = usePermissions();
  const { toast } = useToast();
  const deleteGroup = useDeleteContactGroup();

  if (isLoading) {
    return (
      <Card>
        <div className="py-8 text-center text-sm text-gray-500">Loading...</div>
      </Card>
    );
  }
  if (!group) {
    return (
      <Card>
        <div className="py-8 text-center text-sm text-gray-500">Group not found.</div>
      </Card>
    );
  }

  return (
    <Card padding="none">
      <div className="flex items-start justify-between border-b border-border p-4 dark:border-border-dark">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{group.name}</h2>
            {group.group_type && (
              <Badge variant="info" size="sm">
                {group.group_type}
              </Badge>
            )}
          </div>
          <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
            {group.primary_contact_name
              ? `Billing contact: ${group.primary_contact_name}`
              : 'No billing contact set'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {can(PERMISSIONS.GROUPS_MANAGE_INVOICING) && (
            <Button size="sm" onClick={() => setShowInvoice(true)}>
              Generate Group Invoice
            </Button>
          )}
          {can(PERMISSIONS.GROUPS_UPDATE) && (
            <Button variant="outline" size="sm" onClick={() => onEdit(group)}>
              Edit
            </Button>
          )}
          {can(PERMISSIONS.GROUPS_DELETE) && (
            <Button variant="ghost" size="sm" onClick={() => setShowDelete(true)}>
              Delete
            </Button>
          )}
        </div>
      </div>

      <div className="px-4 pt-3">
        <Tabs
          tabs={[
            { id: 'members', label: 'Members' },
            { id: 'portal', label: 'Portal Access' },
            { id: 'jobs', label: 'Jobs' },
            { id: 'invoices', label: 'Invoices' },
            { id: 'ledger', label: 'Combined Ledger' },
          ]}
          activeTab={tab}
          onChange={setTab}
        />
      </div>

      <div className="p-4">
        {tab === 'members' && <MembersTab group={group} />}
        {tab === 'portal' && <PortalAccessTab group={group} />}
        {tab === 'jobs' && <JobsTab groupId={groupId} />}
        {tab === 'invoices' && <InvoicesTab groupId={groupId} />}
        {tab === 'ledger' && <LedgerTab groupId={groupId} />}
      </div>

      {showInvoice && (
        <GroupInvoiceModal
          groupId={groupId}
          hasPrimary={!!group.primary_contact_id}
          onClose={() => setShowInvoice(false)}
        />
      )}

      <Dialog
        open={showDelete}
        onClose={() => setShowDelete(false)}
        onConfirm={() =>
          deleteGroup.mutate(group.id, {
            onSuccess: () => {
              toast({ variant: 'success', title: 'Group deleted' });
              setShowDelete(false);
              onDeleted();
            },
            onError: () => toast({ variant: 'error', title: 'Failed to delete group' }),
          })
        }
        title={`Delete "${group.name}"?`}
        description="This deletes the group and its memberships. The contacts themselves are not deleted, and their portal access is unaffected."
        confirmLabel="Delete"
        confirmVariant="danger"
        loading={deleteGroup.isPending}
      />
    </Card>
  );
}

// ─── Members tab ──────────────────────────────────────────────────────────

function MembersTab({ group }: { group: ReturnType<typeof useContactGroup>['data'] & object }) {
  const [showAdd, setShowAdd] = useState(false);
  const [toRemove, setToRemove] = useState<GroupMember | null>(null);
  // Which member is being promoted to billing contact (for a per-button spinner).
  const [settingPrimaryId, setSettingPrimaryId] = useState<string | null>(null);
  const removeMember = useRemoveGroupMember(group.id);
  const updateGroup = useUpdateContactGroup(group.id);
  const { toast } = useToast();
  const { can } = usePermissions();
  const canManage = can(PERMISSIONS.GROUPS_UPDATE);
  const { data: relTypes } = useRelationshipTypes();
  const relTypeLabel = (code: string) => relTypes?.find((t) => t.code === code)?.label ?? code;

  const handleRemove = useCallback(() => {
    if (!toRemove) return;
    removeMember.mutate(toRemove.contact_id, { onSuccess: () => setToRemove(null) });
  }, [toRemove, removeMember]);

  const setPrimary = useCallback(
    (contactId: string) => {
      setSettingPrimaryId(contactId);
      updateGroup.mutate(
        { primary_contact_id: contactId },
        {
          onSuccess: () => toast({ variant: 'success', title: 'Billing contact updated' }),
          onError: () => toast({ variant: 'error', title: 'Failed to update billing contact' }),
          onSettled: () => setSettingPrimaryId(null),
        },
      );
    },
    [updateGroup, toast],
  );

  return (
    <div>
      {canManage && (
        <div className="mb-3 flex justify-end">
          <Button size="sm" onClick={() => setShowAdd(true)}>
            Add Member
          </Button>
        </div>
      )}
      {group.members.length === 0 ? (
        <p className="py-6 text-center text-sm text-gray-500 dark:text-gray-400">No members yet.</p>
      ) : (
        <ul role="list" className="divide-y divide-border dark:divide-border-dark">
          {group.members.map((m) => (
            <li key={m.id} className="flex items-center justify-between py-3">
              <div>
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {m.contact_name}
                  </p>
                  {m.is_primary && (
                    <Badge variant="success" size="sm">
                      Billing contact
                    </Badge>
                  )}
                  {m.relationship_type && (
                    <Badge variant="info" size="sm">
                      {relTypeLabel(m.relationship_type)}
                    </Badge>
                  )}
                  {m.portal_role && (
                    <Badge variant={m.portal_role === 'manager' ? 'warning' : 'default'} size="sm">
                      Portal: {m.portal_role === 'manager' ? 'Manager' : 'Normal'}
                    </Badge>
                  )}
                </div>
                {m.email && <p className="text-xs text-gray-500 dark:text-gray-400">{m.email}</p>}
              </div>
              {canManage && (
                <div className="flex items-center gap-2">
                  {!m.is_primary && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setPrimary(m.contact_id)}
                      loading={settingPrimaryId === m.contact_id}
                      disabled={updateGroup.isPending}
                    >
                      Set as billing
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setToRemove(m)}
                    aria-label={`Remove ${m.contact_name}`}
                  >
                    Remove
                  </Button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {showAdd && <AddMemberModal groupId={group.id} onClose={() => setShowAdd(false)} />}

      <Dialog
        open={!!toRemove}
        onClose={() => setToRemove(null)}
        onConfirm={handleRemove}
        title="Remove Member"
        description={
          toRemove
            ? `Remove ${toRemove.contact_name} from this group? This does not delete the contact.`
            : ''
        }
        confirmLabel="Remove"
        confirmVariant="danger"
        loading={removeMember.isPending}
      />
    </div>
  );
}

// ─── Portal Access tab ─────────────────────────────────────────────────────

/** Small inline spinner with an optional label, for in-place saving feedback. */
function Spinner({ label }: { label?: string }) {
  return (
    <span className="inline-flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
      <svg
        className="h-3.5 w-3.5 animate-spin text-brand-600 dark:text-brand-400"
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.4 0 0 5.4 0 12h4z"
        />
      </svg>
      {label && <span>{label}…</span>}
      <span className="sr-only">Saving</span>
    </span>
  );
}

function PortalAccessTab({
  group,
}: {
  group: ReturnType<typeof useContactGroup>['data'] & object;
}) {
  const { toast } = useToast();
  const { can } = usePermissions();
  const canManage = can(PERMISSIONS.GROUPS_UPDATE);
  // Creating a portal user / sending an invite is gated by the client-portal
  // management permission (same as the Settings → Portal Users screen).
  const canManagePortalUsers = can(PERMISSIONS.CLIENTS_MANAGE_PORTAL);

  const setRole = useSetMemberPortalRole(group.id);
  const setAccess = useSetGroupPortalAccess(group.id);
  const setMfa = useSetGroupPortalMfa(group.id);
  const grantPortal = useGrantPortalAccess(group.id);
  // Track which member's role is currently being saved so we can show a
  // per-row spinner and disable just that control while the request is in flight.
  const [savingContactId, setSavingContactId] = useState<string | null>(null);
  // Track which member is being granted portal access (per-button spinner).
  const [grantingContactId, setGrantingContactId] = useState<string | null>(null);

  const handleGrantAccess = (contactId: string) => {
    setGrantingContactId(contactId);
    grantPortal.mutate(contactId, {
      onSuccess: (res) =>
        toast({
          variant: 'success',
          title: res.message || 'Portal invitation sent to the client.',
        }),
      onError: (err: unknown) => {
        const message =
          (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
            ?.message || 'Failed to grant portal access.';
        toast({ variant: 'error', title: message });
      },
      onSettled: () => setGrantingContactId(null),
    });
  };

  const blocked = group.portal_access_blocked;
  const mfaValue: 'default' | 'on' | 'off' =
    group.portal_mfa_enabled === null || group.portal_mfa_enabled === undefined
      ? 'default'
      : group.portal_mfa_enabled
        ? 'on'
        : 'off';

  const managerCount = group.members.filter((m) => m.portal_role === 'manager').length;
  const normalCount = group.members.filter((m) => m.portal_role === 'normal').length;
  const noPortalCount = group.members.filter((m) => !m.has_portal_user).length;

  return (
    <div className="space-y-5">
      {/* Group-level controls */}
      <div className="grid gap-3 sm:grid-cols-2">
        {/* Access card */}
        <div
          className={`rounded-xl border p-4 ${
            blocked
              ? 'border-danger-500/30 bg-danger-50/60 dark:border-danger-500/30 dark:bg-danger-500/10'
              : 'border-success-500/30 bg-success-50/60 dark:border-success-500/30 dark:bg-success-500/10'
          }`}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-2.5">
              <span
                className={`flex h-9 w-9 items-center justify-center rounded-full ${
                  blocked
                    ? 'bg-danger-500/15 text-danger-700 dark:text-danger-500'
                    : 'bg-success-500/15 text-success-700 dark:text-success-500'
                }`}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  aria-hidden="true"
                >
                  {blocked ? (
                    <>
                      <rect x="3" y="11" width="18" height="10" rx="2" />
                      <path d="M7 11V7a5 5 0 0 1 9.9-1" strokeLinecap="round" />
                    </>
                  ) : (
                    <>
                      <rect x="3" y="11" width="18" height="10" rx="2" />
                      <path d="M7 11V7a5 5 0 0 1 10 0v4" strokeLinecap="round" />
                    </>
                  )}
                </svg>
              </span>
              <div>
                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  Portal access
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {blocked
                    ? 'All members are blocked from logging in.'
                    : 'Members can log in to the client portal.'}
                </p>
              </div>
            </div>
            <Badge variant={blocked ? 'danger' : 'success'} size="sm" dot>
              {blocked ? 'Blocked' : 'Allowed'}
            </Badge>
          </div>
          {canManage && (
            <Button
              variant={blocked ? 'primary' : 'outline'}
              size="sm"
              className="mt-3 w-full"
              onClick={() =>
                setAccess.mutate(!blocked, {
                  onSuccess: () =>
                    toast({
                      variant: 'success',
                      title: blocked ? 'Group access granted' : 'Group access blocked',
                    }),
                })
              }
              loading={setAccess.isPending}
            >
              {blocked ? 'Grant access' : 'Block access'}
            </Button>
          )}
        </div>

        {/* MFA card */}
        <div className="rounded-xl border border-border p-4 dark:border-border-dark">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-2.5">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-100 text-brand-600 dark:bg-brand-900/40 dark:text-brand-400">
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  aria-hidden="true"
                >
                  <path
                    d="M12 3l7 4v5c0 4.4-3 7.6-7 9-4-1.4-7-4.6-7-9V7l7-4z"
                    strokeLinejoin="round"
                  />
                  <path d="M9 12l2 2 4-4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
              <div>
                <p className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
                  Emailed MFA
                  {setMfa.isPending && <Spinner label="Saving" />}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Security code emailed at login.
                </p>
              </div>
            </div>
            <Badge
              variant={mfaValue === 'off' ? 'warning' : mfaValue === 'on' ? 'success' : 'info'}
              size="sm"
            >
              {mfaValue === 'on' ? 'Required' : mfaValue === 'off' ? 'Disabled' : 'Default'}
            </Badge>
          </div>
          <div className="mt-3">
            <Select
              value={mfaValue}
              disabled={!canManage || setMfa.isPending}
              onChange={(e) => {
                const v = e.target.value as 'default' | 'on' | 'off';
                const enabled = v === 'default' ? null : v === 'on';
                setMfa.mutate(enabled, {
                  onSuccess: () => toast({ variant: 'success', title: 'Group MFA updated' }),
                });
              }}
              options={[
                { value: 'default', label: 'Platform default (on)' },
                { value: 'on', label: 'Always require' },
                { value: 'off', label: 'Disable' },
              ]}
            />
          </div>
        </div>
      </div>

      {/* Per-member portal roles */}
      <div className="rounded-xl border border-border dark:border-border-dark">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-4 py-3 dark:border-border-dark">
          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">Member roles</p>
          <div className="flex flex-wrap items-center gap-1.5">
            <Badge variant="warning" size="sm">
              {managerCount} manager{managerCount !== 1 ? 's' : ''}
            </Badge>
            <Badge variant="default" size="sm">
              {normalCount} normal
            </Badge>
            {noPortalCount > 0 && (
              <Badge variant="info" size="sm">
                {noPortalCount} no portal account
              </Badge>
            )}
          </div>
        </div>

        {group.members.length === 0 ? (
          <p className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
            No members yet. Add members from the Members tab.
          </p>
        ) : (
          <ul role="list" className="divide-y divide-border dark:divide-border-dark">
            {group.members.map((m) => {
              const initials = m.contact_name
                .split(' ')
                .map((p) => p[0])
                .filter(Boolean)
                .slice(0, 2)
                .join('')
                .toUpperCase();
              return (
                <li key={m.id} className="flex items-center gap-3 px-4 py-3">
                  <span
                    className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
                      m.portal_role === 'manager'
                        ? 'bg-warning-500/15 text-warning-700 dark:text-warning-500'
                        : 'bg-brand-100 text-brand-700 dark:bg-brand-900/40 dark:text-brand-400'
                    }`}
                  >
                    {initials || '?'}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                        {m.contact_name}
                      </p>
                      {m.portal_role && (
                        <Badge
                          variant={m.portal_role === 'manager' ? 'warning' : 'default'}
                          size="sm"
                        >
                          {m.portal_role === 'manager' ? 'Manager' : 'Normal'}
                        </Badge>
                      )}
                    </div>
                    {m.has_portal_user ? (
                      m.email && (
                        <p className="truncate text-xs text-gray-500 dark:text-gray-400">
                          {m.email}
                        </p>
                      )
                    ) : (
                      <p className="flex items-center gap-1 text-xs text-warning-700 dark:text-warning-500">
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          aria-hidden="true"
                        >
                          <path
                            d="M12 9v4m0 4h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        No portal account yet.
                      </p>
                    )}
                  </div>
                  {m.has_portal_user ? (
                    // Portal user exists — let staff set their group portal role.
                    <div className="flex w-56 shrink-0 items-center gap-2">
                      {savingContactId === m.contact_id && <Spinner />}
                      <div className="min-w-0 flex-1">
                        <Select
                          value={m.portal_role ?? ''}
                          disabled={!canManage || savingContactId === m.contact_id}
                          onChange={(e) => {
                            const val = e.target.value;
                            setSavingContactId(m.contact_id);
                            setRole.mutate(
                              {
                                contactId: m.contact_id,
                                portal_role: (val || null) as 'manager' | 'normal' | null,
                              },
                              {
                                onSuccess: () =>
                                  toast({ variant: 'success', title: 'Member role updated' }),
                                onError: () =>
                                  toast({ variant: 'error', title: 'Failed to update role' }),
                                onSettled: () => setSavingContactId(null),
                              },
                            );
                          }}
                          placeholder="No portal role"
                          options={[
                            { value: 'normal', label: 'Normal — own data only' },
                            { value: 'manager', label: 'Manager — all members’ data' },
                          ]}
                        />
                      </div>
                    </div>
                  ) : (
                    // No portal account — offer to create one + send an invite.
                    <div className="w-56 shrink-0 text-right">
                      {canManagePortalUsers ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleGrantAccess(m.contact_id)}
                          loading={grantingContactId === m.contact_id}
                          disabled={grantPortal.isPending}
                        >
                          Grant portal access
                        </Button>
                      ) : (
                        <span className="text-xs text-gray-400 dark:text-gray-500">
                          Invite from their contact page
                        </span>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

function AddMemberModal({ groupId, onClose }: { groupId: string; onClose: () => void }) {
  const [contactId, setContactId] = useState('');
  const [relationshipType, setRelationshipType] = useState('');
  const [portalRole, setPortalRole] = useState<'' | 'manager' | 'normal'>('');
  const [error, setError] = useState('');
  const addMember = useAddGroupMember(groupId);
  const { data: relTypes } = useRelationshipTypes();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!contactId) {
      setError('Select a contact');
      return;
    }
    addMember.mutate(
      {
        contact_id: contactId,
        relationship_type: relationshipType || null,
        portal_role: portalRole || null,
      },
      { onSuccess: onClose, onError: () => setError('Could not add member') },
    );
  };

  return (
    <Modal open onClose={onClose} title="Add Member" size="md">
      <form onSubmit={handleSubmit} className="space-y-4">
        <ContactPicker label="Contact" value={contactId} onChange={setContactId} error={error} />
        <Select
          label="Relationship type (optional)"
          value={relationshipType}
          onChange={(e) => setRelationshipType(e.target.value)}
          placeholder="Select a relationship type"
          options={(relTypes ?? []).map((t) => ({ value: t.code, label: t.label }))}
          hint={
            (relTypes?.length ?? 0) === 0
              ? 'No relationship types configured yet. Add them in Settings.'
              : undefined
          }
        />
        <Select
          label="Portal role (optional)"
          value={portalRole}
          onChange={(e) => setPortalRole(e.target.value as '' | 'manager' | 'normal')}
          placeholder="No portal role"
          options={[
            { value: 'normal', label: 'Normal — sees only their own data' },
            { value: 'manager', label: 'Manager — sees all group members’ data' },
          ]}
          hint="Controls what this client sees in the portal for this group."
        />
        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={addMember.isPending}>
            Cancel
          </Button>
          <Button type="submit" loading={addMember.isPending}>
            Add Member
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// ─── Jobs tab ─────────────────────────────────────────────────────────────

function JobsTab({ groupId }: { groupId: string }) {
  const { data: jobs, isLoading } = useGroupJobs(groupId);
  if (isLoading) return <p className="py-6 text-center text-sm text-gray-500">Loading...</p>;
  if (!jobs || jobs.length === 0)
    return (
      <p className="py-6 text-center text-sm text-gray-500 dark:text-gray-400">
        No jobs across group members.
      </p>
    );
  return (
    <ul role="list" className="divide-y divide-border dark:divide-border-dark">
      {jobs.map((j) => (
        <li key={j.id} className="flex items-center justify-between py-3">
          <div>
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {j.job_code ? `${j.job_code} · ` : ''}
              {j.title}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">{j.client_name}</p>
          </div>
          <div className="flex items-center gap-3">
            <Badge variant="default" size="sm">
              {j.status}
            </Badge>
            <span className="text-sm text-gray-700 dark:text-gray-300">
              {money(j.budget_amount)}
            </span>
          </div>
        </li>
      ))}
    </ul>
  );
}

// ─── Invoices tab ────────────────────────────────────────────────────────

function InvoicesTab({ groupId }: { groupId: string }) {
  const { data: invoices, isLoading } = useGroupInvoices(groupId);
  if (isLoading) return <p className="py-6 text-center text-sm text-gray-500">Loading...</p>;
  if (!invoices || invoices.length === 0)
    return (
      <p className="py-6 text-center text-sm text-gray-500 dark:text-gray-400">
        No invoices across group members.
      </p>
    );
  return (
    <ul role="list" className="divide-y divide-border dark:divide-border-dark">
      {invoices.map((inv) => (
        <li key={inv.id} className="flex items-center justify-between py-3">
          <div>
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {inv.invoice_number}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {inv.client_name} · {inv.issue_date}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Badge variant="default" size="sm">
              {inv.status}
            </Badge>
            <span className="text-sm text-gray-700 dark:text-gray-300">
              {money(inv.outstanding_balance)} / {money(inv.total)}
            </span>
          </div>
        </li>
      ))}
    </ul>
  );
}

// ─── Ledger tab ──────────────────────────────────────────────────────────

function LedgerTab({ groupId }: { groupId: string }) {
  const { data: ledger, isLoading } = useGroupLedger(groupId);
  if (isLoading) return <p className="py-6 text-center text-sm text-gray-500">Loading...</p>;
  if (!ledger) return <p className="py-6 text-center text-sm text-gray-500">No ledger data.</p>;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        <SummaryStat label="Total Debits" value={money(ledger.summary.total_debits)} />
        <SummaryStat label="Total Credits" value={money(ledger.summary.total_credits)} />
        <SummaryStat label="Group Balance" value={money(ledger.summary.balance)} emphasize />
      </div>

      {ledger.members.length > 0 && (
        <div>
          <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Per-member balances
          </h4>
          <ul className="divide-y divide-border dark:divide-border-dark">
            {ledger.members.map((m) => (
              <li key={m.contact_id} className="flex justify-between py-1.5 text-sm">
                <span className="text-gray-700 dark:text-gray-300">{m.contact_name}</span>
                <span className="text-gray-900 dark:text-gray-100">{money(m.balance)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div>
        <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          Combined entries
        </h4>
        {ledger.entries.length === 0 ? (
          <p className="py-4 text-center text-sm text-gray-500 dark:text-gray-400">
            No ledger entries.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase text-gray-500 dark:text-gray-400">
                  <th className="py-2 pr-3">Date</th>
                  <th className="py-2 pr-3">Contact</th>
                  <th className="py-2 pr-3">Type</th>
                  <th className="py-2 pr-3 text-right">Amount</th>
                  <th className="py-2 text-right">Balance</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border dark:divide-border-dark">
                {ledger.entries.map((e) => (
                  <tr key={e.id}>
                    <td className="py-2 pr-3 text-gray-600 dark:text-gray-400">
                      {String(e.entry_date).slice(0, 10)}
                    </td>
                    <td className="py-2 pr-3 text-gray-900 dark:text-gray-100">{e.contact_name}</td>
                    <td className="py-2 pr-3">
                      <Badge variant="default" size="sm">
                        {e.entry_type}
                      </Badge>
                    </td>
                    <td
                      className={`py-2 pr-3 text-right ${e.amount >= 0 ? 'text-gray-900 dark:text-gray-100' : 'text-success-600'}`}
                    >
                      {money(e.amount)}
                    </td>
                    <td className="py-2 text-right text-gray-900 dark:text-gray-100">
                      {money(e.group_running_balance)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function SummaryStat({
  label,
  value,
  emphasize,
}: {
  label: string;
  value: string;
  emphasize?: boolean;
}) {
  return (
    <div className="rounded-lg border border-border p-3 dark:border-border-dark">
      <div className="text-xs text-gray-500 dark:text-gray-400">{label}</div>
      <div
        className={`mt-1 text-lg font-semibold ${emphasize ? 'text-brand-600 dark:text-brand-400' : 'text-gray-900 dark:text-gray-100'}`}
      >
        {value}
      </div>
    </div>
  );
}

// ─── Create/Edit modal ─────────────────────────────────────────────────────

function GroupFormModal({
  open,
  onClose,
  group,
}: {
  open: boolean;
  onClose: () => void;
  group?: ContactGroupSummary;
}) {
  const [name, setName] = useState(group?.name ?? '');
  const [groupType, setGroupType] = useState(group?.group_type ?? '');
  const [description, setDescription] = useState(group?.description ?? '');
  const [error, setError] = useState('');
  const { data: groupTypes } = useGroupTypes();
  const create = useCreateContactGroup();
  const update = useUpdateContactGroup(group?.id ?? '');
  const isEditing = !!group;
  const isPending = create.isPending || update.isPending;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Group name is required');
      return;
    }
    const payload = {
      name: trimmed,
      group_type: groupType || null,
      description: description.trim() || null,
    };
    if (isEditing) {
      update.mutate(payload, { onSuccess: onClose });
    } else {
      create.mutate(payload, { onSuccess: onClose });
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEditing ? 'Edit Group' : 'Create Group'}
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Group name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          error={error}
          placeholder="e.g., Smith Family Group"
          autoFocus
        />
        <Select
          label="Group type"
          value={groupType ?? ''}
          onChange={(e) => setGroupType(e.target.value)}
          placeholder="Select a type (optional)"
          options={(groupTypes ?? []).map((t) => ({ value: t.code, label: t.label }))}
        />
        <div>
          <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Description
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            className="block w-full rounded-lg border border-border bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-border-dark dark:bg-surface-dark-secondary dark:text-gray-100"
          />
        </div>
        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button type="submit" loading={isPending}>
            {isEditing ? 'Save Changes' : 'Create Group'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
